/**
 * API Service
 * Centralized API client with interceptors
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { tagAxiosErrorWithPlanLock } from '@/utils/plan-lock-detection.util';
import { tagAxiosErrorWithPermissionLock } from '@/utils/permission-lock-detection.util';
import {
  tagAxiosErrorWithTenantBlocked,
  detectTenantBlockedResponse,
} from '@/utils/tenant-block-detection.util';
import { consumeSseResponse, type SseFrame } from '@/utils/sse-stream.util';

// Callbacks registered by every mounted useAuth() instance (all equivalent).
// A registry, not a single slot: with one slot, any page using useAuth()
// overwrote the layout's callback and then blanked it on unmount — after which
// a forced logout (expired/revoked session) cleared the tokens but never
// redirected. Each registration returns its own unregister; the most recent
// still-mounted one is used.
function callbackRegistry<T>() {
  const list: T[] = [];
  return {
    register(cb: T): () => void {
      list.push(cb);
      return () => {
        const i = list.lastIndexOf(cb);
        if (i >= 0) list.splice(i, 1);
      };
    },
    current: (): T | null => list[list.length - 1] ?? null,
  };
}

const logoutCallbacks = callbackRegistry<() => void>();
export const setLogoutCallback = (callback: () => void): (() => void) =>
  logoutCallbacks.register(callback);

// Token-sync callbacks. The interceptor refreshes tokens into localStorage, but
// Redux (and anything reading `state.auth.token`) would otherwise keep the
// stale, expired token until a full reload. useAuth wires this to
// dispatch(updateToken) so the store stays in step with localStorage.
const tokenUpdateCallbacks = callbackRegistry<(token: string) => void>();
export const setTokenUpdateCallback = (callback: (token: string) => void): (() => void) =>
  tokenUpdateCallbacks.register(callback);

// Signed out in ANOTHER tab (logout, forced logout, revoked session): the shared
// tokens are gone but this tab's store still says signed in, so it would keep
// polling with no token (401 "No token provided") on a dead page. The 'storage'
// event only fires in the OTHER tabs, never the one that made the change.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'admin_token' && e.newValue === null && e.oldValue !== null) {
      logoutCallbacks.current()?.();
    }
  });
}

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;

  // Single-flight guard for token refresh. Without it, every request that 401s
  // at the same moment (e.g. the parallel dashboard fetches on page load) fires
  // its own POST /auth/refresh — a thundering herd of identical refreshes that
  // also contends on the same User document write. While one refresh is in
  // flight, all other callers await the same promise instead of starting a new
  // one, collapsing N refreshes into 1.
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    // In development, use relative URL to leverage Vite proxy
    // This prevents browser from resolving localhost:5000 relative to subdomain.localhost
    // In production, use absolute URL from environment variable
    const envApiUrl = import.meta.env.VITE_API_URL;

    if (import.meta.env.DEV) {
      // Development: Use relative URL - Vite proxy will handle routing to localhost:5000
      // This ensures API calls go to localhost:5000 regardless of subdomain
      this.baseURL = '/api/v1';
    } else {
      // Production: Use absolute URL from environment or default
      this.baseURL = envApiUrl || 'http://localhost:5000/api/v1';

      // Ensure baseURL is absolute in production
      if (!this.baseURL.startsWith('http://') && !this.baseURL.startsWith('https://')) {
        console.warn(
          'API baseURL should be absolute in production. Using current origin as fallback.'
        );
        this.baseURL = `${window.location.protocol}//${window.location.host}${this.baseURL}`;
      }
    }

    // Debug: Log the base URL being used
    if (import.meta.env.DEV) {
      console.log('🔧 API Service initialized:');
      console.log(`   - Environment: ${import.meta.env.MODE}`);
      console.log(`   - Base URL: ${this.baseURL}`);
      console.log(
        `   - Current hostname: ${typeof window !== 'undefined' ? window.location.hostname : 'N/A'}`
      );
      console.log(`   - Vite proxy will route /api/* to http://localhost:5000`);
    }

    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds
    });

    // Request interceptor - add auth token
    // Tenant is resolved from JWT token tenantId by backend, no subdomain needed
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Debug: Log request URL to help diagnose URL issues
        if (import.meta.env.DEV) {
          const fullUrl = config.baseURL
            ? config.url?.startsWith('http')
              ? config.url
              : `${config.baseURL}${config.url}`
            : config.url;
          console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${fullUrl}`);
          console.log(`   - Base URL: ${config.baseURL}`);
          console.log(`   - Request URL: ${config.url}`);
          console.log(`   - Tenant resolved from JWT token tenantId`);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Only a JWT auth failure should trigger a token refresh + retry.
        // Other 401s (e.g. WHATSAPP_REAUTH_REQUIRED for an expired Meta
        // access token) are domain-specific and would otherwise cause an
        // infinite refresh/retry loop since refreshing the JWT never fixes
        // them. The `_retry` flag also caps any retry to a single attempt.
        // Also exclude the refresh endpoint itself: a 401 there (e.g. the
        // refresh token belongs to a user who was just disabled) must go
        // straight to logout — otherwise this branch calls `/auth/refresh`
        // again, which 401s again, which calls `/auth/refresh` again... an
        // unbounded self-retry loop since the nested call gets a brand new
        // config with `_retry` unset.
        const errorCode = error.response?.data?.error?.code;
        const isRefreshEndpoint = !!error.config?.url?.includes('/auth/refresh');
        const isJwtAuthError =
          error.response?.status === 401 &&
          ['TOKEN_EXPIRED', 'INVALID_TOKEN', 'UNAUTHORIZED'].includes(errorCode) &&
          !error.config?._retry &&
          !isRefreshEndpoint;

        if (isRefreshEndpoint && error.response?.status === 401) {
          this.handleLogout();
          tagAxiosErrorWithPlanLock(error);
          tagAxiosErrorWithPermissionLock(error);
          return Promise.reject(error);
        }

        if (isJwtAuthError) {
          error.config._retry = true;
          try {
            // Shared single-flight refresh (see refreshAccessToken). Returns the
            // new access token, or null when there is no refresh token to use —
            // in which case we fall through and reject exactly as before, without
            // forcing a logout.
            const newToken = await this.refreshAccessToken();
            if (newToken) {
              // Retry original request. The request interceptor re-attaches the
              // freshly-stored token from localStorage on this replay.
              return this.client(error.config);
            }
          } catch (refreshError) {
            // Refresh failed - logout properly
            this.handleLogout();
          }
        }

        // Tag plan/quota-restriction and RBAC permission-denial errors so
        // callers can route them to the global PlanLockDialog/
        // PermissionLockDialog instead of their own inline error Alert.
        tagAxiosErrorWithPlanLock(error);
        tagAxiosErrorWithPermissionLock(error);
        // Suspended / inactive workspace (read-only): a refused write fires the global
        // 'workspace is read-only' dialog — see tenant-block-detection.util.ts.
        tagAxiosErrorWithTenantBlocked(error);
        return Promise.reject(error);
      }
    );
  }

  private handleLogout() {
    // Clear tokens
    localStorage.removeItem('admin_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('admin_user');

    // Use callback if available (for Redux logout), otherwise redirect
    const logoutCallback = logoutCallbacks.current();
    if (logoutCallback) {
      logoutCallback();
    } else {
      // Fallback: redirect to login
      window.location.href = '/login';
    }
  }

  /**
   * For non-HTTP consumers (the realtime socket): refresh the access token via
   * the same single-flight refresh the interceptor uses. Same contract as
   * refreshAccessToken — null when there's no refresh token, rejects when the
   * session can't be refreshed (caller should then forceLogout()).
   */
  refreshSession(): Promise<string | null> {
    return this.refreshAccessToken();
  }

  /** The same logout a failed refresh triggers: clear tokens, store, redirect. */
  forceLogout(): void {
    this.handleLogout();
  }

  /**
   * Refresh the access token, coalescing concurrent callers onto a single
   * in-flight request (see `refreshPromise`). Resolves to the new access token
   * on success, or `null` when there is no refresh token stored. Rejects only
   * when the refresh request itself fails (invalid/expired refresh token,
   * network error) — callers treat that as "log out".
   *
   * On success it writes both tokens to localStorage AND notifies Redux via
   * tokenUpdateCallback, so every token consumer (axios, raw fetch, streaming)
   * reads the same fresh value.
   */
  private refreshAccessToken(): Promise<string | null> {
    // A refresh is already running — everyone waits on the same one.
    if (this.refreshPromise) return this.refreshPromise;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return Promise.resolve(null);

    this.refreshPromise = this.post('/auth/refresh', { refreshToken })
      .then((response) => {
        if (response?.success && response.data?.tokens?.accessToken) {
          const accessToken: string = response.data.tokens.accessToken;
          localStorage.setItem('admin_token', accessToken);
          if (response.data.tokens.refreshToken) {
            localStorage.setItem('refresh_token', response.data.tokens.refreshToken);
          }
          // Keep Redux (and the socket/consumers that read it) in step.
          tokenUpdateCallbacks.current()?.(accessToken);
          return accessToken;
        }
        return null;
      })
      .finally(() => {
        // Clear so the NEXT expiry starts a fresh single-flight refresh.
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete(url, config);
    return response.data;
  }

  async upload<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  /**
   * Get current user with fresh module access
   */
  async getCurrentUser(): Promise<any> {
    return this.get('/auth/me');
  }

  /**
   * POST that reads a `text/event-stream` response, invoking `onEvent` per
   * frame as it arrives. Plain `fetch`, not axios — axios's browser streaming
   * support varies by adapter, and a native ReadableStream reader is the
   * standard way to consume SSE from a POST (EventSource only supports GET).
   *
   * Because this bypasses the axios interceptors above, a failed response is
   * shaped into an axios-LIKE error (`err.response.data`) and run through the
   * same plan-lock / permission-lock taggers the interceptor applies. That
   * keeps the established call-site pattern working unchanged:
   *   `if (!showPlanLockIfTagged(err) && !isPermissionLockTagged(err)) setError(...)`
   * Without it a QUOTA_EXCEEDED on a streaming route would silently degrade
   * from the global PlanLockDialog to a plain inline message.
   *
   * A 401 for an expired JWT IS refreshed-and-retried here (see below), because
   * it arrives before any stream bytes — nothing has been consumed, so the retry
   * is safe. It shares the same single-flight refresh as the axios interceptor.
   * A mid-stream failure is still not retried (that WOULD replay a half-consumed
   * stream); the caller re-sends in that case.
   */
  async streamPost(
    url: string,
    data: unknown,
    onEvent: (frame: SseFrame) => void,
    options?: { signal?: AbortSignal }
  ): Promise<void> {
    const doFetch = (token: string | null) =>
      fetch(`${this.baseURL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
        ...(options?.signal ? { signal: options.signal } : {}),
      });

    let res = await doFetch(localStorage.getItem('admin_token'));

    // This path bypasses the axios interceptor, so replicate its refresh-and-
    // retry for an expired JWT here. A 401 arrives before any stream bytes are
    // sent, so nothing has been consumed and a single retry is safe (unlike a
    // mid-stream failure). Uses the same single-flight refresh as every other
    // request. `res.clone()` reads the error code without consuming the body the
    // generic error-shaping block below still relies on for non-auth 401s.
    if (res.status === 401) {
      let code: string | undefined;
      try {
        code = (await res.clone().json())?.error?.code;
      } catch {
        /* body wasn't JSON — leave code undefined */
      }
      if (code && ['TOKEN_EXPIRED', 'INVALID_TOKEN', 'UNAUTHORIZED'].includes(code)) {
        let newToken: string | null = null;
        try {
          newToken = await this.refreshAccessToken();
        } catch {
          newToken = null;
        }
        if (newToken) {
          res = await doFetch(newToken);
        } else {
          this.handleLogout();
        }
      }
    }

    if (!res.ok) {
      let body: any = null;
      try {
        body = await res.json();
      } catch {
        /* body wasn't JSON — fall back to the generic message below */
      }
      const error: any = new Error(
        body?.error?.message || body?.message || `Request failed (${res.status})`
      );
      // Mirror the axios error shape the taggers below read from, so the
      // global plan/permission dialogs behave identically on this path.
      error.response = { status: res.status, data: body };
      tagAxiosErrorWithPlanLock(error);
      tagAxiosErrorWithPermissionLock(error);
      throw error;
    }

    await consumeSseResponse(res, onEvent);
  }

  /**
   * A `fetch` that carries the JWT and self-heals on expiry, for the handful of
   * call sites that must use raw `fetch` instead of the axios client (they read
   * a full URL and parse the body themselves). It attaches a fresh access token,
   * and on a 401 for an expired JWT it runs the SAME single-flight refresh the
   * axios interceptor uses and retries once. Without this, those requests bypass
   * the interceptor entirely and surface a raw "Token expired" that only a full
   * page reload clears.
   *
   * Returns the raw `Response` so existing call sites keep doing `await r.json()`
   * unchanged. Any `Authorization` header in `init.headers` is overridden with
   * the current token; all other headers (Content-Type, method, body, subdomain)
   * are preserved.
   */
  async authorizedFetch(input: string, init: RequestInit = {}): Promise<Response> {
    const withAuth = (token: string | null): RequestInit => ({
      ...init,
      headers: {
        ...((init.headers as Record<string, string> | undefined) ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    let res = await fetch(input, withAuth(localStorage.getItem('admin_token')));

    if (res.status === 401) {
      let code: string | undefined;
      try {
        code = (await res.clone().json())?.error?.code;
      } catch {
        /* body wasn't JSON — leave code undefined */
      }
      if (code && ['TOKEN_EXPIRED', 'INVALID_TOKEN', 'UNAUTHORIZED'].includes(code)) {
        let newToken: string | null = null;
        try {
          newToken = await this.refreshAccessToken();
        } catch {
          newToken = null;
        }
        if (newToken) {
          res = await fetch(input, withAuth(newToken));
        } else {
          this.handleLogout();
        }
      }
    }

    // Raw-fetch (streaming/SSE) callers bypass the axios interceptor, so give a
    // read-only-workspace refusal the same global dialog here.
    await detectTenantBlockedResponse(res);

    return res;
  }
}

export default new ApiService();
